import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthContextType, User, LoginCredentials } from '../types';
import { authAPI } from '../services/api';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    console.log('AuthContext: Checking for saved token:', savedToken);
    if (savedToken) {
      setToken(savedToken);
      loadCurrentUser();
    } else {
      console.log('AuthContext: No token found');
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCurrentUser = async () => {
    try {
      console.log('AuthContext: Loading current user...');
      const currentUser = await authAPI.getCurrentUser();
      console.log('AuthContext: Current user loaded:', currentUser);
      setUser(currentUser);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('AuthContext: Failed to load current user:', error);
      // Если токен невалидный, разлогиниваемся
      const savedToken = localStorage.getItem('token');
      if (savedToken) {
        console.log('AuthContext: Token is invalid, logging out');
        logout();
      } else {
        setIsLoading(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (credentials: LoginCredentials) => {
    console.log('Login attempt with credentials:', credentials.username);
    try {
      console.log('Making API call to login...');
      const response = await authAPI.login(credentials.username, credentials.password);
      console.log('Login response:', response);
      setToken(response.access_token);
      localStorage.setItem('token', response.access_token);
      setIsAuthenticated(true);
      await loadCurrentUser();
    } catch (error: any) {
      console.error('Login failed:', error);
      console.error('Error response:', error.response);
      console.error('Error data:', error.response?.data);
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
    localStorage.removeItem('token');
  };

  const updateUser = (newUserData: Partial<User>) => {
    if (user) {
      setUser({ ...user, ...newUserData });
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, updateUser, isAuthenticated, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};